/* ----------------- React --------------- */
import { useCallback, useState } from "react";

/* ----------------- Types --------------- */
import type { Distribution } from "@memorang/types";

import { useExamContext } from "@features/exam";
import { useProethicsFlowStore } from "@features/proethics-simulation";
/* ----------------- Hooks --------------- */
import useSessionStore from "./useSessionStore";

/* ----------------- Analytics --------------- */
import { trackCustomEvent } from "analytics";

/* ----------------- Constants --------------- */
import { events } from "@constants/tracking";

import {
	getMockedSessionResponse,
	getProethicaLiveCreateParams,
	getProethicaTestCreateParams,
	liveSessionEnabled,
	normalizeProethicaSessionResponse,
} from "@constants/demo";
import { getProethicaTestByCollectionId } from "@constants/demo/proethica-tests";
import type { ProethicaModuleSummary } from "@constants/demo/session-collection/proethica";
import { isWeb } from "@helpers/platform";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import { flattenSessionItems } from "@helpers/session";
import useAppConfigIds from "@hooks/useAppConfigIds";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import type { SessionResponse } from "@memorang/types/src/session/new-session";
import { useToast } from "@memorang/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useWorkflowStore from "components/WorkflowItem/useWorkflowStore";
/* ----------------- Router --------------- */
import { useRouter } from "expo-router";
import { createSession, endSession } from "../api/mutations";
import type { CreateSessionRequest } from "../api/types";
import { useEMRStore } from "../components/emr-card/useEMRStore";
import useAIFeedbackStore from "./useAIFeedbackStore";
import { useProethicaSessionStore } from "./useProethicaSessionStore";
import { useProethicaSessionTimeStore } from "./useProethicaSessionTimeStore";
import { useProethicaTestItemStatesStore } from "./useProethicaTestItemStatesStore";
import { useResumeSession } from "./useResumeSession";

let pendingEndSessionPromise: Promise<unknown> | null = null;
let pendingEndSessionId: number | null = null;

const awaitPendingEndSession = async () => {
	if (pendingEndSessionPromise) {
		await pendingEndSessionPromise;
	}
};

export enum LearningModes {
	questions = "question",
	writing = "writing",
	listening = "listening",
	speaking = "speaking",
	reading = "reading",
	flashcards = "flashcards",
	quiz = "quiz",
}

export const useSession = () => {
	const {
		currentExamContext: { studyPackProductId },
	} = useExamContext();
	const router = useRouter();

	const { showToast } = useToast();
	const { handleResumeSession } = useResumeSession();

	const [showErrorDialog, setShowErrorDialog] = useState(false);
	const updateSession = useSessionStore((state) => state.updateSession);
	const resetAiFeedbackStore = useAIFeedbackStore(
		(state) => state.resetAiFeedbackStore,
	);

	const resetWorkflowStore = useWorkflowStore(
		(state) => state.resetWorkflowStore,
	);
	const resetEMRStore = useEMRStore((state) => state.resetUserData);
	const { currentExam } = useDemoExamStore();
	const examId = currentExam?.id;

	const { organizationId, projectId, collectionId, userId } = useAppConfigIds();

	const queryClient = useQueryClient();
	const updateEndingSessionInProgress = useSessionStore(
		(state) => state.updateEndingSessionInProgress,
	);

	const toggleShowErrorDialog = () => {
		setShowErrorDialog((prev) => !prev);
	};

	const createSessionMutation = useMutation({
		mutationFn: createSession,
	});

	const endSessionMutation = useMutation({
		mutationFn: endSession,
		onSuccess: () => {
			updateEndingSessionInProgress(false);
			queryClient.invalidateQueries();
		},
	});

	const createNewSession = useCallback(
		(request: CreateSessionRequest) => {
			return createSessionMutation.mutateAsync(request);
		},
		[createSessionMutation],
	);

	const resetSessionSideEffects = useCallback(() => {
		resetAiFeedbackStore();
		resetWorkflowStore();
		resetEMRStore();
		useProethicsFlowStore.getState().reset();
	}, [resetAiFeedbackStore, resetWorkflowStore, resetEMRStore]);

	const endCurrentSession = ({
		id,
		userId,
	}: { id: number; userId: number }) => {
		if (!liveSessionEnabled(examId)) {
			updateEndingSessionInProgress(false);
			return;
		}
		if (pendingEndSessionId === id && pendingEndSessionPromise) {
			return pendingEndSessionPromise;
		}

		const {
			proethicaTestKind,
			itemStates,
			numericId: storeNumericId,
			moduleId: storeModuleId,
			timeElapsed,
		} = useSessionStore.getState();
		if (proethicaTestKind) {
			const { saveTestItemStates } = useProethicaTestItemStatesStore.getState();
			saveTestItemStates(id, itemStates);
			if (storeNumericId && storeNumericId !== id) {
				saveTestItemStates(storeNumericId, itemStates);
			}
		} else if (storeModuleId) {
			useProethicaSessionTimeStore
				.getState()
				.saveSessionElapsed(id, storeNumericId, timeElapsed);
		}
		updateEndingSessionInProgress(true);
		const endPromise = endSessionMutation
			.mutateAsync({ sessionId: id, userId })
			.catch((error) => {
				captureErrorToSentry(new Error("Error ending session"), {
					error: JSON.stringify(error),
				});
			})
			.finally(() => {
				updateEndingSessionInProgress(false);
				if (pendingEndSessionPromise === endPromise) {
					pendingEndSessionPromise = null;
					pendingEndSessionId = null;
				}
			});
		pendingEndSessionPromise = endPromise;
		pendingEndSessionId = id;
		return endPromise;
	};

	const handlePostSessionCreation = ({
		newSessionResponse,
		numItemsRequested,
		dist,
		tags,
		handleClose,
		replace,
		isDiagnostic,
		moduleId,
		moduleSummary,
	}: {
		newSessionResponse: SessionResponse;
		numItemsRequested: number;
		dist: Distribution;
		tags?: number[] | string[];
		handleClose?: () => void;
		replace?: boolean;
		isDiagnostic?: boolean;
		moduleId?: string;
		moduleSummary?: ProethicaModuleSummary;
	}) => {
		trackCustomEvent({
			eventName: events.sessionStarted,
			numItems: numItemsRequested,
			dist: JSON.stringify(dist),
			tags: JSON.stringify(tags || []),
			contentId: studyPackProductId,
			type: isDiagnostic ? "DIAGNOSTIC_SESSION" : "FORMATIVE_SESSION",
		});
		updateSession({
			sessionResponse: newSessionResponse,
			numItemsRequested,
			isSummativeTest: false,
			isDiagnostic,
			moduleId,
			moduleSummary,
		});
		handleClose?.();
		(replace ? router.replace : router.push)({
			pathname: "/(protected)/session/[id]",
			params: {
				id: newSessionResponse.id,
			},
		});
	};

	const getSessionResponse = useCallback(() => {
		const mockedSessionResponse = normalizeProethicaSessionResponse(
			getMockedSessionResponse(examId),
		);

		if (isWeb) {
			return mockedSessionResponse;
		}

		const filteredSessionItems = mockedSessionResponse.sessionItems.map(
			(item) => {
				return {
					...item,
					items: item.items?.filter(
						(item) => item.metadata?.type !== "MARKING_ANSWER",
					),
				};
			},
		);

		return {
			...mockedSessionResponse,
			sessionItems: filteredSessionItems,
		};
	}, [examId]);

	const resumeTrackedModuleSession = useCallback(
		async (moduleId: string, replace = false): Promise<boolean> => {
			const tracked =
				useProethicaSessionStore.getState().sessionByModule[moduleId];
			if (!tracked) {
				return false;
			}
			const outcome = await handleResumeSession({
				numericId: tracked.numericId,
				replace,
				moduleId,
			});
			if (outcome === "failed") {
				useProethicaSessionStore.getState().clearModuleSession(moduleId);
				return false;
			}
			return true;
		},
		[handleResumeSession],
	);

	const waitForLiveEndAndResumeTracked = useCallback(
		async (live: boolean, moduleId?: string, replace = false) => {
			if (!live) {
				return false;
			}
			await awaitPendingEndSession();
			return moduleId ? resumeTrackedModuleSession(moduleId, replace) : false;
		},
		[resumeTrackedModuleSession],
	);

	const resumeTrackedTestSession = useCallback(
		async (testId: string, replace = false): Promise<boolean> => {
			if (!useProethicaSessionStore.getState().sessionByModule[testId]) {
				return false;
			}
			resetSessionSideEffects();
			useDemoExamStore.getState().setCurrentModuleId(testId);
			return waitForLiveEndAndResumeTracked(true, testId, replace);
		},
		[resetSessionSideEffects, waitForLiveEndAndResumeTracked],
	);

	const buildCreateParams = useCallback(
		(args: {
			live: boolean;
			selectedModuleId?: string;
			numItemsRequested: number;
			dist: Distribution;
			tags?: number[] | string[];
			itemIds?: number[];
			additionalSessionParams?: {
				title: string;
				icon: string;
				sessionSubType?: "DIAGNOSTIC";
				examId?: string;
			};
		}): CreateSessionRequest => {
			if (args.live) {
				return getProethicaLiveCreateParams(args.selectedModuleId);
			}
			const { additionalSessionParams } = args;
			return {
				userId,
				collectionId,
				projectId,
				organizationId,
				sessionType: additionalSessionParams ? "SUMMATIVE" : "FORMATIVE",
				sessionSubType: additionalSessionParams?.sessionSubType,
				tagIds: (args.tags as number[]) || [],
				itemIds: args.itemIds,
				itemsSelected: args.numItemsRequested,
				itemsSelectedDistribution: args.dist,
				title: additionalSessionParams?.title,
				icon: additionalSessionParams?.icon,
				examId: additionalSessionParams?.examId,
			};
		},
		[userId, collectionId, projectId, organizationId],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: // TODO fix me later
	const handleCreateFormativeSession = useCallback(
		async ({
			dist,
			tags,
			handleClose,
			itemIds,
			replace,
			additionalSessionParams,
		}: {
			dist: Distribution;
			tags?: number[] | string[];
			handleClose?: () => void;
			itemIds?: number[];
			replace?: boolean;
			additionalSessionParams?: {
				title: string;
				icon: string;
				sessionSubType?: "DIAGNOSTIC";
				examId?: string;
			};
		}) => {
			resetSessionSideEffects();

			const live = liveSessionEnabled(examId);
			const selectedModuleId = useDemoExamStore.getState().currentModuleId;

			if (
				await waitForLiveEndAndResumeTracked(live, selectedModuleId, replace)
			) {
				return;
			}

			try {
				const numItemsRequested = Object.values(dist).reduce(
					(acc, curr) => acc + curr,
					0,
				);

				const params = buildCreateParams({
					live,
					selectedModuleId,
					numItemsRequested,
					dist,
					tags,
					itemIds,
					additionalSessionParams,
				});

				const result = live
					? normalizeProethicaSessionResponse(await createNewSession(params))
					: getSessionResponse();

				const createErrors = (
					result as { errors?: { title?: string; message?: string } }
				).errors;
				if (createErrors || !result.sessionItems?.length) {
					showToast({
						message:
							createErrors?.message ?? "Failed to create session, try again",
					});
					return;
				}

				if (live && selectedModuleId && result.numericId) {
					useProethicaSessionStore
						.getState()
						.setModuleSession(selectedModuleId, {
							numericId: result.numericId,
						});
				}

				const flattenedSessionItems = flattenSessionItems(result.sessionItems);

				// const newSessionResponse = finalMockedSessionResponse;

				const newSessionResponse = {
					...result,
					sessionItems: flattenedSessionItems,
				};

				handlePostSessionCreation({
					newSessionResponse,
					numItemsRequested,
					dist,
					tags,
					handleClose,
					replace,
					isDiagnostic:
						additionalSessionParams?.sessionSubType === "DIAGNOSTIC",
					moduleId: selectedModuleId,
					moduleSummary: result.moduleSummary,
				});
			} catch (_error) {
				toggleShowErrorDialog();
				captureErrorToSentry(new Error("Error creating formative session"), {
					error: JSON.stringify(_error),
				});
			}
		},
		[
			router,
			studyPackProductId,
			updateSession,
			createNewSession,
			waitForLiveEndAndResumeTracked,
			buildCreateParams,
			resetSessionSideEffects,
		],
	);

	const defaultDist: Distribution = { low: 0, medium: 0, high: 0, none: 10 };

	const handleDirectSessionStart = useCallback(
		({ replace, moduleId }: { replace?: boolean; moduleId?: string } = {}) => {
			useDemoExamStore
				.getState()
				.setCurrentModuleId(examId === "proethica" ? moduleId : undefined);
			return handleCreateFormativeSession({
				dist: defaultDist,
				tags: [],
				replace,
			});
		},
		[examId, handleCreateFormativeSession],
	);

	// Always creates a fresh summative test session. Completed tests are routed
	// to their report and paused tests are resumed upstream
	// (useProethicaModuleList.startModule); the report's "Retake post-test"
	// button calls here directly to bypass both guards.
	// biome-ignore lint/correctness/useExhaustiveDependencies: toggleShowErrorDialog is recreated each render; mirrors handleCreateFormativeSession.
	const handleStartProethicaTest = useCallback(
		async ({ testId, replace }: { testId: string; replace?: boolean }) => {
			const descriptor = getProethicaTestByCollectionId(testId);
			if (!descriptor) {
				return;
			}

			resetSessionSideEffects();
			useDemoExamStore.getState().setCurrentModuleId(testId);

			await awaitPendingEndSession();

			try {
				const result = normalizeProethicaSessionResponse(
					await createNewSession(getProethicaTestCreateParams(descriptor.kind)),
				);

				const createErrors = (
					result as { errors?: { title?: string; message?: string } }
				).errors;
				if (createErrors || !result.sessionItems?.length) {
					showToast({
						message: createErrors?.message ?? "Failed to start test, try again",
					});
					return;
				}

				// Tracked so a completed test can resolve its report even before the
				// live distribution's exam.numericId refreshes.
				if (result.numericId) {
					useProethicaSessionStore.getState().setModuleSession(testId, {
						numericId: result.numericId,
					});
				}

				const newSessionResponse = {
					...result,
					sessionItems: flattenSessionItems(result.sessionItems),
				};
				const testNumItems = newSessionResponse.sessionItems.length;

				trackCustomEvent({
					eventName: events.sessionStarted,
					numItems: testNumItems,
					dist: JSON.stringify({ none: testNumItems }),
					tags: JSON.stringify([]),
					contentId: studyPackProductId,
					type: "SUMMATIVE_SESSION",
				});

				updateSession({
					sessionResponse: newSessionResponse,
					numItemsRequested: testNumItems,
					isSummativeTest: true,
					proethicaTestKind: descriptor.kind,
					moduleId: testId,
					moduleSummary: result.moduleSummary,
				});

				(replace ? router.replace : router.push)({
					pathname: "/(protected)/session/[id]",
					params: {
						id: newSessionResponse.id,
					},
				});
			} catch (_error) {
				toggleShowErrorDialog();
				captureErrorToSentry(
					new Error("Error starting proethica test session"),
					{
						error: JSON.stringify(_error),
					},
				);
			}
		},
		[
			examId,
			studyPackProductId,
			resetSessionSideEffects,
			createNewSession,
			updateSession,
			router,
			showToast,
		],
	);

	return {
		handleCreateFormativeSession,
		handleDirectSessionStart,
		handleStartProethicaTest,
		resumeTrackedTestSession,
		showErrorDialog,
		toggleShowErrorDialog,
		createNewSession,
		endCurrentSession,
		isCreatingSession: createSessionMutation.isPending,
		isEndingSession: endSessionMutation.isPending,
		createSessionError: createSessionMutation.error,
		endSessionError: endSessionMutation.error,
	};
};
